<template>
    <div>
        <card class="cursor-pointer mb-2">
            <div class="p-3 text-center" @click="open()">
                <img src="/img/healthjump-logo.png" style="max-height: 60px"/>
            </div>
        </card>

        <modal
            ref="modal"
            :size="size"
            :has-footer="false"
            :scrollable="true"
            @hide="close()">
            <template slot="title">
                Healthjump Integration
            </template>

            <transition name="fade-in-down">
                <div v-if="!loading">
                    <div v-if="!isEdit && !logsMode">
                        <div class="card-header d-flex pb-1">
                            <p class="text-dark">
                                {{ integration.client }} <br>
                                <small class="text-muted">
                                    Campaign: {{ integration.campaign_name || 'not configured' }}
                                </small>
                            </p>
                            <div class="ml-auto">
                                <button title="Logs"
                                    type="button"
                                    class="btn btn-default btn-sm btn-icon ml-1"
                                    @click="loadLogs()">
                                    <i class="fa fa-fw fa-history"></i>
                                </button>

                                <button title="Edit"
                                    type="button"
                                    class="btn btn-default btn-sm btn-icon ml-1"
                                    @click="startEdit()">
                                    <i class="fa fa-fw fa-pencil-alt"></i>
                                </button>
                            </div>
                        </div>
                        <div class="card-body p-1">
                            <div class="text-right pr-2">
                                <small class="font-italic text-muted">
                                    Last Synced at:
                                    {{ integration.last_synced_at || 'not synced'}}
                                </small>
                            </div>
                        </div>
                    </div>

                    <div v-if="isEdit">
                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group"
                                    :class="{'is-invalid': form.errors.has('campaign_id')}">
                                    <label>Campaign</label>
                                    <select class="form-control" v-model="form.campaign_id">
                                        <option :value="null"></option>
                                        <option v-for="campaign in location.campaigns"
                                            :key="campaign.id" :value="campaign.id">
                                            {{ campaign.name }}
                                        </option>
                                    </select>
                                    <p v-if="form.errors.has('campaign_id')" class="invalid-feedback">
                                        {{ form.errors.get('campaign_id') }}
                                    </p>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <label>&nbsp;</label>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group"
                                    :class="{'is-invalid': form.errors.has('sync_at')}">
                                    <div class="d-flex justify-between">
                                        <label>Sync at</label>
                                        <small class="ml-auto float-right text-muted"
                                            style="padding-top: 4px">
                                            Server Time:
                                            <server-time
                                                class="ml-1"
                                                format="hh:mm:ss a"
                                            />
                                        </small>
                                    </div>

                                    <vue-ctk-date-time-picker
                                        id="sync-at"
                                        v-model="form.sync_at"
                                        label="Select time"
                                        :no-label="true"
                                        format="hh:mm a"
                                        formatted="hh:mm a"
                                        color="#0374D7"
                                        :minute-interval="1"
                                        :only-time="true"
                                    />
                                    <p v-if="form.errors.has('sync_at')" class="invalid-feedback">
                                        {{ form.errors.get('sync_at') }}
                                    </p>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="form-group"
                                    :class="{'is-invalid': form.errors.has('send_at')}">
                                    <label>Send invites at</label>
                                    <vue-ctk-date-time-picker
                                        id="send-at"
                                        v-model="form.send_at"
                                        label="Select time"
                                        :no-label="true"
                                        color="#0374D7"
                                        format="hh:mm a"
                                        formatted="hh:mm a"
                                        :without-header="true"
                                        :minute-interval="1"
                                        :only-time="true"
                                    />
                                    <p v-if="form.errors.has('send_at')" class="invalid-feedback">
                                        {{ form.errors.get('send_at') }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="form-group" v-if="integration.locations_available.length">
                            <div class="mb-3 d-flex align-items-center">
                                <legend class="m-0 w-auto font-weight-bold">
                                    Locations
                                </legend>
                                <div class="ml-3 custom-control custom-checkbox">
                                    <input type="checkbox"
                                        class="custom-control-input"
                                        id="locationsAll"
                                        v-model="locationsAll"
                                        @change="selectAllLocations">
                                    <label class="custom-control-label" for="locationsAll">
                                        Select all
                                    </label>
                                </div>
                            </div>
                            <div class="alert alert-info tw-text-sm">
                                Please, select at least one location or provider in order to retrieve data.
                            </div>
                            <div class="row">
                                <div class="col-md-6 checkbox"
                                    v-for="(location, index) in integration.locations_available"
                                    :key="index">
                                    <label class="checkbox-inline px-2">
                                        <input v-model="form.locations" type="checkbox"
                                            :value="location"
                                            @change="selectedAllLocations">
                                        {{ location }}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div class="form-group" v-if="integration.providers_available.length">
                            <div class="mb-3 d-flex align-items-center">
                                <legend class="m-0 w-auto font-weight-bold">
                                    Providers
                                </legend>
                                <div class="ml-3 custom-control custom-checkbox">
                                    <input type="checkbox"
                                           class="custom-control-input"
                                           id="providersAll"
                                           v-model="providersAll"
                                           @change="selectAllProviders">
                                    <label class="custom-control-label" for="providersAll">
                                        Select all
                                    </label>
                                </div>
                            </div>
                            <div class="alert alert-info tw-text-sm">
                                Please, select at least one location or provider in order to retrieve data.
                            </div>
                            <div class="row">
                                <div class="col-md-6 checkbox"
                                     v-for="(provider, index) in integration.providers_available"
                                     :key="index">
                                    <label class="checkbox-inline px-2">
                                        <input v-model="form.providers" type="checkbox"
                                               :value="provider"
                                               @change="selectedAllProviders">
                                        {{ provider }}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div class="form-group" v-if="integration.types_available.length">
                            <hr>
                            <div class="mb-3 d-flex align-items-center">
                                <legend class="m-0 w-auto font-weight-bold">
                                    Types
                                </legend>
                                <div class="ml-3 custom-control custom-checkbox">
                                    <input type="checkbox"
                                        class="custom-control-input"
                                        id="typesAll"
                                        v-model="typesAll"
                                        @change="selectAllTypes">
                                    <label class="custom-control-label" for="typesAll">
                                        Select all
                                    </label>
                                </div>
                            </div>
                            <div class="alert alert-info tw-text-sm">
                                If no types are selected they will all be considered. When you select one or more types only those selected will be considered.
                            </div>
                            <div class="row">
                                <div class="col-md-6 checkbox"
                                    v-for="(type, index) in integration.types_available"
                                    :key="index">
                                    <label class="checkbox-inline px-2">
                                        <input v-model="form.types" type="checkbox"
                                            :value="type"
                                            @change="selectedAllTypes">
                                        {{ type }}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div class="form-group" v-if="integration.client_id == 'OGC01'">
                            <hr>
                            <div class="mb-3 d-flex align-items-center">
                                <legend class="m-0 w-auto font-weight-bold">
                                    eCW Types
                                </legend>
                                <div class="ml-3 custom-control custom-checkbox">
                                    <input type="checkbox"
                                           class="custom-control-input"
                                           id="ecwTypesAll"
                                           v-model="ecwTypesAll"
                                           @change="selectAllEcwTypes">
                                    <label class="custom-control-label" for="ecwTypesAll">
                                        Select all
                                    </label>
                                </div>
                            </div>
                            <div class="alert alert-info tw-text-sm">
                                If no eCW types are selected they will all be considered. When you select one or more eCW types only those selected will be considered.
                            </div>
                            <div class="row">
                                <div class="col-md-6 checkbox"
                                     v-for="(ecwType, index) in ecwTypesAvailable"
                                     :key="index">
                                    <label class="checkbox-inline px-2">
                                        <input v-model="form.ecw_types" type="checkbox"
                                               :value="ecwType"
                                               @change="selectedAllEcwTypes">
                                        {{ ecwType }}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div class="form-group" v-if="integration.status_available.length">
                            <hr>
                            <div class="mb-3 d-flex align-items-center">
                                <legend class="m-0 w-auto font-weight-bold">
                                    Status
                                </legend>
                                <div class="ml-3 custom-control custom-checkbox">
                                    <input type="checkbox"
                                        class="custom-control-input"
                                        id="statusAll"
                                        v-model="statusAll"
                                        @change="selectAllStatus">
                                    <label class="custom-control-label" for="statusAll">
                                        Select all
                                    </label>
                                </div>
                            </div>
                            <div class="alert alert-info tw-text-sm">
                                If no statuses are selected they will all be considered. When you select one or more statuses only those selected will be considered.
                            </div>
                            <div class="row">
                                <div class="col-md-4 checkbox"
                                    v-for="(status, index) in integration.status_available"
                                    :key="index">
                                    <label class="checkbox-inline px-2">
                                        <input v-model="form.status" type="checkbox"
                                            :value="status"
                                            @change="selectedAllStatus">
                                        {{ status }}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div class="modal-footer mt-2 pr-0 pb-0 pl-0">
                            <button type="button" class="btn btn-default" @click="cancelEdit()">
                                Cancel
                            </button>
                            <button-loading type="button" class="btn btn-primary ml-2"
                                :loading="form.submitting"
                                @click="save">
                                Save
                            </button-loading>
                        </div>
                    </div>

                    <div v-if="logsMode">
                        <card-spinner
                            style="height:200px"
                            v-if="loadingLogs"
                        />

                        <template v-else>
                            <div style="margin: 5px;">
                                <v-server-table
                                    ref="logsTable"
                                    style="font-size: small; white-space: nowrap;"
                                    :url="'healthjump/' + this.integration.id + '/encounter-logs'"
                                    :columns="columns"
                                    :options="options">
                                    <template slot="result_status" slot-scope="props" >
                                        <span v-if="props.row.result_status !== 'success'"
                                              class="badge px-2 py-1 text-uppercase"
                                              @click="toggleStatus(props.row.id)"
                                              :class="statusClassOf(props.row)">
                                                    {{ props.row.result_status  }}
                                                    <i class="fa fa-chevron-down"></i>
                                        </span>
                                        <span v-else class="badge px-2 py-1 text-uppercase"
                                              :class="statusClassOf(props.row)">
                                                {{ props.row.result_status  }}
                                        </span>
                                    </template>
                                    <template slot="child_row" slot-scope="props" >
                                        <div :id="props.row.id" class="p-1">
                                            <pre>{{ props.row.errors }}</pre>
                                        </div>
                                    </template>
                                </v-server-table>
                            </div>
                            <div class="modal-footer mt-2 pr-0 pb-0 pl-0">
                                <button @click="closeLogs()"
                                    class="btn btn-default mt-2">
                                    Back
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </transition>

            <card-spinner
                v-if="loading"
                style="height:200px"
            />
        </modal>
    </div>
</template>

<script>
    import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
    import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';

    export default {
        name: "HealthjumpIntegration",

        components: {
            VueCtkDateTimePicker
        },

        props: {
            location: {required: true}
        },

        data: () => ({
            form       : new Form(),
            loading    : false,
            integration: {},
            isEdit     : false,
            logsMode   : false,
            logs       : [],
            loadingLogs: false,
            ecwTypesAvailable: ["1", "2", "3", "4", "5", "6", "7", "8", "9"],

            locationsAll: false,
            providersAll: false,
            typesAll    : false,
            ecwTypesAll : false,
            statusAll   : false,

            columns     : [
                "result_status",
                "encounter_date",
                "patient_name",
                "encounter_location",
                "encounter_provider",
                "encounter_type",
                "ecw_type",
                "status"
            ],
            options     : {
                perPage: 25,
                skin: "table table-hover table-striped table-light table-sm mb-0",
                descOrderColumns: ['encounter_date'],
                headings: {
                    "result_status"     : "Result",
                    "encounter_date"    : "Date",
                    "patient_name"      : "Patient",
                    "encounter_location": "Location",
                    "encounter_provider": "Provider",
                    "encounter_type"    : "Type",
                    "ecw_type"          : "eCW Type",
                    "status"            : "Status"
                },
                orderBy: {
                    column: 'encounter_date',
                    ascending: false
                },
                texts:{
                    filterPlaceholder:"Patient, Location, or Provider"
                },
                responseAdapter: function (response) {
                    this.loadingLogs = false;
                    return { data: response.data.data, count: response.data.meta.total };
                }
            }
        }),

        computed: {
            size() {
                return !this.logsMode ? 'lg' : 'xl';
            }
        },

        methods: {

            toggleStatus(id) {
                this.$refs.logsTable.toggleChildRow(id);
            },

            async open() {
                this.$refs.modal.open();
                this.$emit('show', true);

                this.loadIntegration();
            },

            close() {
                this.$emit('hide');
                this.logsMode       = false;
                this.logs           = [];
                this.integration    = {};
                this.cancelEdit();

            },

            cancelEdit() {
                this.isEdit = false;
                this.form = new Form();
            },

            async loadIntegration() {

                this.loading = true;

                await this.$http.get(this.$laroute.route('healthjump.index', {
                    location: this.location.id
                }))
                .then(({data}) => {
                    this.integration = data.data;
                    this.loading     = false;

                    if(this.integration.client_id != 'OGC01'){
                        this.options['hiddenColumns'] = ['ecw_type']
                    }
                });
            },

            startEdit() {
                this.isEdit = true;
                this.form = new Form({
                    id         : this.integration.id,
                    campaign_id: this.integration.campaign_id,
                    sync_at    : this.integration.sync_at || '12:00 am',
                    send_at    : this.integration.send_at || '12:00 am',
                    locations  : this.integration.locations || [],
                    providers  : this.integration.providers || [],
                    types      : this.integration.types || [],
                    ecw_types  : this.integration.ecw_types || [],
                    status     : this.integration.status || [],
                });
                this.selectedAllLocations();
                this.selectedAllTypes();
                this.selectedAllStatus();
            },

            async loadLogs() {
                this.logsMode = true;
                await this.fetchEncounterLogs();
            },

            async fetchEncounterLogs(page = 1) {
                this.loadingLogs = true;
                const params = {
                    healthjump: this.integration.id,
                    page
                };
                await this.$http.get(this.$laroute.route('healthjump.encounter-logs', params))
                .then(({data}) => {
                    this.logs        = data;
                    this.loadingLogs = false;
                });
            },

            statusClassOf(log) {
                if (log.result_status === 'success') {
                    return 'badge-success';
                }

                if (log.result_status === 'failed') {
                    return 'badge-danger cursor-pointer';
                }

                if (log.result_status === 'filter-off') {
                    return 'badge-secondary cursor-pointer';
                }

                return null;
            },

            closeLogs() {
                this.logsMode = false;
            },

            selectAllLocations() {
                if (this.locationsAll) {
                    this.form.locations = this.integration.locations_available;
                    return;
                }

                this.form.locations = [];
            },

            selectedAllLocations() {
                if (_.isEqual(_.sortBy(this.integration.locations_available), _.sortBy(this.form.locations))) {
                    this.locationsAll = true;
                    return;
                }

                this.locationsAll = false;
            },

            selectAllProviders() {
                if (this.providersAll) {
                    this.form.providers = this.integration.providers_available;
                    return;
                }

                this.form.providers = [];
            },

            selectedAllProviders() {
                if (_.isEqual(_.sortBy(this.integration.providers_available), _.sortBy(this.form.providers))) {
                    this.providersAll = true;
                    return;
                }

                this.providersAll = false;
            },

            selectAllTypes() {
                if (this.typesAll) {
                    this.form.types = this.integration.types_available;
                    return;
                }

                this.form.types = [];
            },

            selectedAllTypes() {
                if (_.isEqual(_.sortBy(this.integration.types_available), _.sortBy(this.form.types))) {
                    this.typesAll = true;
                    return;
                }

                this.typesAll = false;
            },

            selectAllEcwTypes() {
                if (this.ecwTypesAll) {
                    this.form.ecw_types = this.ecwTypesAvailable;
                    return;
                }

                this.form.ecw_types = [];
            },

            selectedAllEcwTypes() {
                if (_.isEqual(_.sortBy(this.ecwTypesAvailable), _.sortBy(this.form.ecw_types))) {
                    this.ecwTypesAll = true;
                    return;
                }

                this.ecwTypesAll = false;
            },

            selectAllStatus() {
                if (this.statusAll) {
                    this.form.status = this.integration.status_available;
                    return;
                }

                this.form.status = [];
            },

            selectedAllStatus() {
                if (_.isEqual(_.sortBy(this.integration.status_available), _.sortBy(this.form.status))) {
                    this.statusAll = true;
                    return;
                }

                this.statusAll = false;
            },

            async save() {
                return await this.form.put(this.$laroute.route('healthjump.update', {
                    healthjump: this.form.id
                }))
                .then(() => {
                    swal.fire({
                        title: 'Saved!',
                        type: 'success',
                    }).then(() => {
                        this.loadIntegration();
                        this.cancelEdit();
                    });
                })
                .catch(error => console.error(error));
            },
        }
    }
</script>

<style lang="scss" scoped>
    legend {
        font-size: 1rem;
    }

    .is-invalid {
        .invalid-feedback {
            display: block;
        }
    }

    .table th,
    .table td {
        white-space: nowrap;
        font-size: small;
    }
</style>

<style lang="scss">
    .VueTables__search__input {
        width: 250px !important;
    }
    .VuePagination {
        margin-top: 10px;
    }
</style>
